import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Dropdown, Modal } from 'react-bootstrap'
import DatePicker from 'react-datepicker'

import FilterPagination from 'pages/parts/FilterPagination'
import { GET, httpOk } from 'helpers/api'
import { formatDate, formatMoney, chequeDates, getCurrentChequeDate, checkFilter } from 'helpers/helpers'

function DeletedCheques() {
	const { t } = useTranslation()
	const date = new Date()

	const initialFilter = {
		start_date: formatDate(new Date(date.getFullYear(), date.getMonth(), 1), 'yyyy-MM-dd'),
		end_date:   formatDate(new Date(), 'yyyy-MM-dd'),
		search:     '',
	}

	const [data, setData]               = useState({ data: [], total: 0, total_amount: 0 })
	const [filterData, setFilterData]   = useState({ ...initialFilter })
	const [filterDataExist, setFilterDataExist] = useState(false)
	const [dropdownIsOpen, setDropdownIsOpen]   = useState(false)
	const [selectedCheque, setSelectedCheque]   = useState(null)
	const [pagination, setPagination]   = useState({ url: '/report/deleted-cheques', response: null })

	async function getData() {
		setFilterDataExist(checkFilter(filterData, initialFilter))
		const response = await GET('/report/deleted-cheques', filterData, { loader: true })
		if (httpOk(response)) {
			setData(response.data)
			setPagination({ ...pagination, response: response.data })
			if (dropdownIsOpen) setDropdownIsOpen(false)
		}
	}

	async function paginate(page, size) {
		setFilterData({ ...filterData, page, size })
	}

	function resetFilter() {
		setFilterData({ ...initialFilter })
	}

	async function selectChequeDates(option) {
		const result = getCurrentChequeDate(option.id)
		setFilterData({ ...filterData, ...result, dateType: option.id })
	}

	useEffect(() => {
		getData()
	}, [filterData.page, filterData.size]) // eslint-disable-line react-hooks/exhaustive-deps

	return (
		<>
			<div className="page-header d-flex justify-content-between">
				<h4 className="vertical-center">
					{t('deleted_cheques')} [{data.total}]
				</h4>
				<div className="vertical-center">
					<span className="text-muted me-2">{t('total')}:</span>
					<span className="text-danger fw-bold fs-5">{formatMoney(data.total_amount)}</span>
				</div>
			</div>

			<div className="card">
				<div className="card-body">
					<div className="d-flex justify-content-between mb-3">
						<div className="d-flex gap-2">
							<Dropdown onToggle={() => setDropdownIsOpen(!dropdownIsOpen)} show={dropdownIsOpen}>
								<Dropdown.Toggle variant="outline-primary" className="btn-wide h-100">
									{t('filter')}
								</Dropdown.Toggle>

								<Dropdown.Menu className="dropdown-filter">
									<div className="row">
										<div className="form-group col-md-4">
											<label>{t('period')}</label>
											<select className="form-select"
												onChange={(e) => selectChequeDates({ id: e.target.value })}
												value={filterData.dateType || ''}>
												<option value="">{t('select')}</option>
												{chequeDates.map(opt => (
													<option key={opt.id} value={opt.id}>{opt.name}</option>
												))}
											</select>
										</div>
										<div className="form-group col-md-4">
											<label className="invisible">.</label>
											<div className="calendar-input">
												<DatePicker
													className="form-control"
													dateFormat="dd.MM.yyyy"
													selected={filterData.start_date ? new Date(filterData.start_date) : ''}
													onChange={(d) => setFilterData({ ...filterData, start_date: formatDate(d, 'yyyy-MM-dd') })} />
												<i className="uil uil-calendar-alt"></i>
											</div>
										</div>
										<div className="form-group col-md-4">
											<label className="invisible">.</label>
											<div className="calendar-input">
												<DatePicker
													className="form-control"
													dateFormat="dd.MM.yyyy"
													selected={filterData.end_date ? new Date(filterData.end_date) : ''}
													onChange={(d) => setFilterData({ ...filterData, end_date: formatDate(d, 'yyyy-MM-dd') })} />
												<i className="uil uil-calendar-alt"></i>
											</div>
										</div>
									</div>

									<div className="row mt-3">
										<div className="form-group col-md-12">
											<label>{t('search')}</label>
											<input type="text" className="form-control"
												value={filterData.search}
												onChange={(e) => setFilterData({ ...filterData, search: e.target.value })}
												placeholder={t('search')} />
										</div>
									</div>

									<div className="row mt-3">
										<div className="col-md-12 d-flex justify-content-end gap-2">
											<button className="btn btn-secondary" onClick={() => setDropdownIsOpen(false)}>{t('cancel')}</button>
											<button className="btn btn-primary" onClick={getData}>{t('apply')}</button>
										</div>
									</div>
								</Dropdown.Menu>
							</Dropdown>
						</div>
					</div>

					{filterDataExist &&
						<div className="filter-block">
							<div className="filter">
								<strong>{t('filter')}</strong>
								<br />
								{(filterData.start_date || filterData.end_date) &&
									<span className="me-2">
										<b>{t('period')}: </b>
										{filterData.start_date && <span>{formatDate(filterData.start_date)}</span>}
										{filterData.start_date && filterData.end_date && <span> | </span>}
										{filterData.end_date && <span>{formatDate(filterData.end_date)}</span>}
									</span>
								}
								{filterData.search &&
									<span className="me-2">
										<b>{t('search')}: </b>{filterData.search}
									</span>
								}
							</div>
							<div className="vertical-center">
								<button className="btn btn-outline-dark text-uppercase" onClick={resetFilter}>{t('reset')}</button>
							</div>
						</div>
					}

					<div className="table-responsive">
						<table className="table table-bordered mb-0">
							<thead>
								<tr>
									<th style={{ minWidth: '40px' }}>#</th>
									<th style={{ minWidth: '60px' }}>{t('cheque')} №</th>
									<th style={{ minWidth: '120px' }}>{t('client')}</th>
									<th style={{ minWidth: '120px' }}>{t('cashier')}</th>
									<th style={{ minWidth: '100px' }}>{t('deleted_by')}</th>
									<th style={{ minWidth: '130px' }}>{t('sale_date')}</th>
									<th style={{ minWidth: '130px' }}>{t('delete_date')}</th>
									<th style={{ minWidth: '80px' }} className="text-end">{t('amount')}</th>
									<th style={{ minWidth: '160px' }}>{t('delete_note')}</th>
									<th style={{ minWidth: '40px' }}></th>
								</tr>
							</thead>
							<tbody>
								{data?.data?.map((item, index) => (
									<tr key={item.id} className="tr-middle">
										<td>{index + 1}</td>
										<td className="fw-bold text-danger">#{item.id}</td>
										<td>{item.client_name || '—'}</td>
										<td>{item.cashier_name || '—'}</td>
										<td>
											<span className="badge bg-danger">
												{item.deleted_by_name || item.deleted_by || '—'}
											</span>
										</td>
										<td>{item.cheque_closed_at ? formatDate(item.cheque_closed_at, 'dd.MM.yyyy HH:mm') : '—'}</td>
										<td>{item.deleted_at ? formatDate(item.deleted_at, 'dd.MM.yyyy HH:mm') : '—'}</td>
										<td className="text-end fw-bold">{formatMoney(item.total_amount)}</td>
										<td className="text-muted fz-12">{item.delete_note || '—'}</td>
										<td>
											<button
												className="btn btn-sm btn-outline-secondary"
												onClick={() => setSelectedCheque(item)}
												title={t('products')}>
												<i className="uil uil-eye"></i>
											</button>
										</td>
									</tr>
								))}
							</tbody>
						</table>
					</div>

					{pagination.response &&
						<FilterPagination pagination={pagination} paginate={paginate} />
					}
				</div>
			</div>

			{/* Mahsulotlar modali */}
			<Modal show={!!selectedCheque} animation={false} centered size="lg"
				onHide={() => setSelectedCheque(null)}>
				<Modal.Header closeButton>
					<Modal.Title>
						{t('cheque')} #{selectedCheque?.id}
						{selectedCheque?.client_name && <span className="text-muted ms-2 fz-14">— {selectedCheque.client_name}</span>}
					</Modal.Title>
				</Modal.Header>
				<Modal.Body>
					<div className="row mb-3">
						<div className="col-6">
							<div className="d-between">
								<span className="text-muted">{t('cashier')}</span>
								<span>{selectedCheque?.cashier_name || '—'}</span>
							</div>
							<div className="d-between">
								<span className="text-muted">{t('sale_date')}</span>
								<span>{selectedCheque?.cheque_closed_at ? formatDate(selectedCheque.cheque_closed_at, 'dd.MM.yyyy HH:mm') : '—'}</span>
							</div>
						</div>
						<div className="col-6">
							<div className="d-between">
								<span className="text-muted">{t('deleted_by')}</span>
								<span className="text-danger fw-bold">{selectedCheque?.deleted_by_name || selectedCheque?.deleted_by || '—'}</span>
							</div>
							<div className="d-between">
								<span className="text-muted">{t('delete_date')}</span>
								<span>{selectedCheque?.deleted_at ? formatDate(selectedCheque.deleted_at, 'dd.MM.yyyy HH:mm') : '—'}</span>
							</div>
						</div>
					</div>

					{selectedCheque?.delete_note &&
						<div className="alert alert-warning mb-3">
							<b>{t('delete_note')}:</b> {selectedCheque.delete_note}
						</div>
					}

					<table className="table table-bordered mb-0">
						<thead>
							<tr>
								<th>#</th>
								<th>{t('product_name')}</th>
								<th>{t('roll')}</th>
								<th className="text-end">{t('quantity')}</th>
								<th className="text-end">{t('metr')}</th>
								<th className="text-end">{t('price')}</th>
								<th className="text-end">{t('amount')}</th>
							</tr>
						</thead>
						<tbody>
							{selectedCheque?.products?.map((p, i) => {
								const isRoll = [3, 4, 5].includes(Number(p.product_type))
								return (
									<tr key={i}>
										<td>{i + 1}</td>
										<td>{p.product_name}</td>
										<td>{p.element_name || '—'}</td>
										<td className="text-end">{formatMoney(p.quantity, isRoll ? 0 : 3)}</td>
										<td className="text-end">
											{isRoll
												? <span className="fw-bold text-primary">{formatMoney(p.metr, 2)}</span>
												: <span className="text-muted">—</span>
											}
										</td>
										<td className="text-end">{formatMoney(p.sale_price)}</td>
										<td className="text-end fw-bold">{formatMoney(p.total_price)}</td>
									</tr>
								)
							})}
						</tbody>
						<tfoot className="table-secondary fw-bold">
							<tr>
								<td colSpan="6" className="text-end">{t('total')}</td>
								<td className="text-end text-danger">{formatMoney(selectedCheque?.total_amount)}</td>
							</tr>
						</tfoot>
					</table>
				</Modal.Body>
			</Modal>
		</>
	)
}

export default DeletedCheques
